import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Menu,
  MenuItem,
  Popover
} from '@material-ui/core';
import { ChevronDown } from 'lucide-react';
import { ReactNode, useMemo, useRef, useState } from 'react';
import { useUnsafeTranslation } from '../../i18next';
import { ChipWithAction } from '../ChipWithAction';
import { PopoverContainer } from '../ColumnSelector';

/**
 * Matching rule for a selection managed by a `ChipFilterWithDropdown` rendered with
 * `exclusiveAll`: an empty selection is the "All" state and matches every key, not none.
 * Filtering code driven by such a selection should use this instead of a raw
 * `.includes()`/`.length` check
 */
export const matchesExclusiveAllFilter = <T extends string>(
  selectedKeys: T[],
  key: T
): boolean => selectedKeys.length === 0 || selectedKeys.includes(key);

export const ChipFilterWithSelect = <T extends string>({
  value,
  onChange,
  options,
  size = 'small',
  color,
  backgroundColor
}: {
  value: T;
  onChange: (nextValue: T) => void;
  options: { value: T; label: React.ReactNode }[];
  /** Pass 'medium' to render the trigger at the taller control size. */
  size?: 'small' | 'medium';
  /** Override the trigger text/icon color (defaults to near-black). */
  color?: string;
  /** Override the trigger fill (defaults to grey). */
  backgroundColor?: string;
}) => {
  const { t } = useUnsafeTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  const theme = useTheme();
  const close = () => setOpen(false);
  const triggerColor = color ?? theme.custom.colors.gray.black;
  const triggerBg = backgroundColor ?? theme.custom.colors.gray.w300;
  return (
    <>
      <div ref={ref}>
        <ChipWithAction
          size={size}
          onClick={() => setOpen((x) => !x)}
          label={selected?.label || t('Nothing selected')}
          color={triggerColor}
          backgroundColor={triggerBg}
          icon={
            <ChevronDown
              size={size === 'medium' ? 18 : 16}
              strokeWidth={1.5}
              stroke={triggerColor}
            />
          }
        />
      </div>
      <Menu
        keepMounted
        anchorEl={ref.current}
        open={open}
        onClose={() => close()}
      >
        {options.map((o) => (
          <MenuItem
            selected={o === selected}
            key={o.value}
            onClick={() => {
              onChange(o.value);
              close();
            }}
          >
            {o.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export const ChipFilterWithDropdown = <T extends string = string>({
  label,
  selectedOptions,
  onChange,
  options,
  renderTrigger,
  exclusiveAll
}: {
  label: string;
  options: { key: T; label: string }[];
  selectedOptions: { key: T; label: string }[];
  onChange: (nextValue: { key: T; label: string }[]) => void;
  /** Overrides the default ChipWithAction trigger, e.g. for a differently-styled surface. */
  renderTrigger?: (props: { onClick: () => void }) => ReactNode;
  /**
   * "All" represents no filter (nothing individually selected) rather than every option
   * being individually ticked — checking "All" clears the selection, and it visually
   * unchecks as soon as any individual option is selected. Also adds a divider between
   * the "All" row and the option list. Opt-in so existing multi-select-only consumers of
   * this component are unaffected.
   */
  exclusiveAll?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [openPop, setOpenPop] = useState(false);
  const selectedKeys = useMemo(
    () => new Set(selectedOptions.map((v) => v.key)),
    [selectedOptions]
  );

  const theme = useTheme();
  const formControlLabelStyle = {
    root: css({
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`
    })
  };

  return (
    <>
      <div ref={ref}>
        {renderTrigger ? (
          renderTrigger({ onClick: () => setOpenPop((x) => !x) })
        ) : (
          <ChipWithAction
            onClick={() => setOpenPop((x) => !x)}
            label={label}
            color={theme.custom.colors.gray.black}
            backgroundColor={theme.custom.colors.gray.w300}
            icon={
              <ChevronDown
                size={16}
                strokeWidth={1.5}
                stroke={theme.custom.colors.gray.black}
              />
            }
          />
        )}
      </div>
      <Popover
        open={openPop}
        onClose={() => setOpenPop(false)}
        anchorEl={ref.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <PopoverContainer>
          <div
            style={
              exclusiveAll
                ? { borderBottom: `1px solid ${theme.custom.colors.gray.w300}` }
                : undefined
            }
          >
            <FormControlLabel
              classes={formControlLabelStyle}
              control={
                <Checkbox
                  checked={
                    exclusiveAll
                      ? selectedKeys.size === 0
                      : selectedKeys.size >= options.length
                  }
                  color="primary"
                  onChange={(ev) => {
                    if (exclusiveAll) {
                      onChange([]);
                    } else {
                      ev.target.checked ? onChange(options) : onChange([]);
                    }
                  }}
                />
              }
              label={t('All')}
            />
          </div>
          <FormControl component="fieldset">
            <FormGroup>
              {options.map((a) => (
                <div key={a.key}>
                  <FormControlLabel
                    classes={formControlLabelStyle}
                    control={
                      <Checkbox
                        checked={selectedKeys.has(a.key)}
                        color="primary"
                        onChange={(ev) => {
                          const nextSet = new Set([...selectedKeys]);
                          if (ev.target.checked) {
                            nextSet.add(a.key);
                          } else {
                            nextSet.delete(a.key);
                          }
                          onChange(options.filter((x) => nextSet.has(x.key)));
                        }}
                      />
                    }
                    label={a.label}
                  />
                </div>
              ))}
            </FormGroup>
          </FormControl>
        </PopoverContainer>
      </Popover>
    </>
  );
};
